import { energyAnalysisApiSlice as api } from '../../../apiSlice';
export const addTagTypes = ['Portfolio Analysis'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getMatrikelAnalysisMatrikelAnalysisAccountIdGet: build.query<
        GetMatrikelAnalysisMatrikelAnalysisAccountIdGetApiResponse,
        GetMatrikelAnalysisMatrikelAnalysisAccountIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/matrikel_analysis/${queryArg.accountId}`,
          params: {
            degree_day_correction: queryArg.degreeDayCorrection,
          },
        }),
        providesTags: ['Portfolio Analysis'],
      }),
      getMatrikelMetadataMatrikelMetadataAccountIdGet: build.query<
        GetMatrikelMetadataMatrikelMetadataAccountIdGetApiResponse,
        GetMatrikelMetadataMatrikelMetadataAccountIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/matrikel_metadata/${queryArg.accountId}`,
        }),
        providesTags: ['Portfolio Analysis'],
      }),
      getSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGet:
        build.query<
          GetSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGetApiResponse,
          GetSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGetApiArg
        >({
          query: (queryArg) => ({
            url: `/matrikel_metadata/${queryArg.accountId}/${queryArg.matrikelKey}`,
          }),
          providesTags: ['Portfolio Analysis'],
        }),
      matrikelOverwriteMatrikelOverwriteAccountIdMatrikelKeyPatch:
        build.mutation<
          MatrikelOverwriteMatrikelOverwriteAccountIdMatrikelKeyPatchApiResponse,
          MatrikelOverwriteMatrikelOverwriteAccountIdMatrikelKeyPatchApiArg
        >({
          query: (queryArg) => ({
            url: `/matrikel_overwrite/${queryArg.accountId}/${queryArg.matrikelKey}`,
            method: 'PATCH',
            body: queryArg.matrikelOverwritePatchInput,
          }),
          invalidatesTags: ['Portfolio Analysis'],
        }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as energyAnalysisOpenApi };
export type GetMatrikelAnalysisMatrikelAnalysisAccountIdGetApiResponse =
  /** status 200 Successful Response */ MatrikelAnalysisOutput;
export type GetMatrikelAnalysisMatrikelAnalysisAccountIdGetApiArg = {
  /** The ReCalc account id to analyse. */
  accountId: number;
  /** Whether to apply degree day correction to the analysis. Default is False. */
  degreeDayCorrection?: boolean;
};
export type GetMatrikelMetadataMatrikelMetadataAccountIdGetApiResponse =
  /** status 200 Successful Response */ MatrikelDataPoint[];
export type GetMatrikelMetadataMatrikelMetadataAccountIdGetApiArg = {
  /** The ReCalc account id to analyse. */
  accountId: number;
};
export type GetSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGetApiResponse =
  /** status 200 Successful Response */ MatrikelDataPoint;
export type GetSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGetApiArg =
  {
    /** The ReCalc account id to analyse. */
    accountId: number;
    /** The matrikel key to get the metadata for. */
    matrikelKey: string;
  };
export type MatrikelOverwriteMatrikelOverwriteAccountIdMatrikelKeyPatchApiResponse =
  /** status 200 Successful Response */ MatrikelOverwritePatchOutput;
export type MatrikelOverwriteMatrikelOverwriteAccountIdMatrikelKeyPatchApiArg =
  {
    /** The account id to update the matrikel overwrite data for. */
    accountId: number;
    /** The matrikel key to update the overwrite data for. */
    matrikelKey: string;
    matrikelOverwritePatchInput: MatrikelOverwritePatchInput;
  };
export type ApiWarning = {
  /** The warning message */
  message: string;
  /** The warning type */
  warning_type: string;
  /** The warning details */
  details?: object;
};
export type MatrikelAnalysisOutput = {
  /** The matrikel analysis data. */
  MatrikelAnalysis: object[];
  Warnings?: ApiWarning[];
};
export type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};
export type HttpValidationError = {
  detail?: ValidationError[];
};
export type BuildingDataPoint = {
  /** The address of the building. */
  AdresseBetegnelse: string;
  /** The matrikel key. */
  MatrikelKey: string;
  /** The BFE number of the building. */
  BFENummer: number;
  /** The building number associated with the Matrikel Key. */
  Bygningsnummer: number;
  /** The street name of the building. */
  StreetName: string;
  /** The house number of the building. */
  StreetNumber: string;
  /** The postal code of the building. */
  ZipCode: number;
};
export type MatrikelDataPoint = {
  /** The matrikel key. */
  MatrikelKey: string;
  /** The BFE number. */
  BFENummer: number;
  /** The buildings associated with the matrikel. */
  Buildings: BuildingDataPoint[];
  /** The owner code of the matrikel. */
  EjerlavKode: number;
  /** The owner name of the matrikel. */
  EjerlavNavn: string;
  /** The municipality code of the matrikel. */
  KommuneKode: number;
  /** The matrikel number. */
  MatrikelNr: string;
  /** The average building year. */
  AverageBuildingYear?: number | null;
  /** The sum building area. */
  SumBuildingArea?: number | null;
  /** The total buildings. */
  TotalBuildings?: number | null;
  /** The extracted building use. */
  ExtractedBuildingUse?: string | null;
  /** The extracted heat installation. */
  ExtractedHeatInstallation?: string | null;
  /** The building uses with area. */
  BuildingUsesWithArea?: string | null;
  /** The original building uses with area. */
  OriginalBuildingUsesWithArea?: string | null;
  /** The heat installations with area. */
  HeatInstallationsWithArea?: string | null;
  /** The EMO integration success. */
  EMOIntegrationSuccess?: boolean | null;
  /** The energy label classification. */
  EnergyLabelClassification?: string | null;
  /** The energy label count overview. */
  EnergyLabelCountOverview?: string | null;
  /** If some of the benchmark data is overwritten by the user. */
  OverwriteFlag?: boolean;
  /** If the benchmark data is verified by the user. */
  VerificationFlag?: boolean;
  /** The overwritten extracted building use. */
  OverwriteExtractedBuildingUse?: string | null;
  /** The overwritten extracted heat installation. */
  OverwriteExtractedHeatInstallation?: string | null;
  /** The overwritten average building year. */
  OverwriteAverageBuildingYear?: number | null;
  /** The overwritten sum building area. */
  OverwriteSumBuildingArea?: number | null;
};
export type MatrikelOverwritePatchOutput = {
  /** The matrikel key. */
  MatrikelKey: string;
  /** The average building year. */
  AverageBuildingYear: number | null;
  /** The sum building area. */
  SumBuildingArea: number | null;
  /** The extracted building use. */
  ExtractedBuildingUse: string | null;
  /** The extracted heat installation. */
  ExtractedHeatInstallation: string | null;
  /** Indicates that some of the benchmark data is overwritten by the user. */
  OverwriteFlag?: boolean;
  /** If the benchmark data is verified by the user and can be used for the benchmark. */
  VerificationFlag?: boolean;
};
export type MatrikelOverwritePatchInput = {
  /** The average building year. */
  AverageBuildingYear: number | null;
  /** The sum building area. */
  SumBuildingArea: number | null;
  /** The extracted building use. */
  ExtractedBuildingUse: string | null;
  /** The extracted heat installation. */
  ExtractedHeatInstallation: string | null;
  /** Indicates that some of the benchmark data is overwritten by the user. */
  OverwriteFlag?: boolean;
  /** If the benchmark data is verified by the user and can be used for the benchmark. */
  VerificationFlag?: boolean;
};
export const {
  useGetMatrikelAnalysisMatrikelAnalysisAccountIdGetQuery,
  useGetMatrikelMetadataMatrikelMetadataAccountIdGetQuery,
  useGetSingleMatrikelMetadataMatrikelMetadataAccountIdMatrikelKeyGetQuery,
  useMatrikelOverwriteMatrikelOverwriteAccountIdMatrikelKeyPatchMutation,
} = injectedRtkApi;
